import * as dataSelectors from '@/data/data-selectors';
import { PropertyLicenseStatus } from '@/property-details/components/property-license-status';
import { signS3Url } from '@/utils/sign-s3-url';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import placeholderImg from '../../img/placeholders/city-placeholder.jpg';
import { MAPBOX_DEFAULT_STYLE_SUBPATH } from '../map/map-constants';
import { StaticLocationMapImage } from '../property-details/components/details-info-modules/location-mod';
import { firstImageUrl } from '../utils/data-utils';
import { dayjs } from '../utils/dayjs';
import ReactImageFallback from '../utils/image-fallback-loader';
import { generateFullUrl } from '../utils/string-utils';
import { MonthlyCalendar } from './components/monthly-calendar';
import { parseComponentsFromAddressSafe } from './utils/address-parser';

const PropertyHistoryContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  font-family: League Spartan;
`;

const StyledStaticLocationMapImage = styled(StaticLocationMapImage)`
  margin: 0;
`;
const PropertyDetails = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PropertyBasicDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  justify-content: center;
`;

const Address = styled.h3``;

const PROPERTY_IMAGES_HEIGHT_PX = 120;

const PropertyImages = styled.div`
  height: ${PROPERTY_IMAGES_HEIGHT_PX}px;
  display: flex;
  gap: 10px;
`;

const PropertyReactImageFallback = styled(ReactImageFallback)`
  height: ${PROPERTY_IMAGES_HEIGHT_PX}px;
`;

const StyledCalendar = styled(MonthlyCalendar)`
  flex: 1;
`;

const Footnote = styled.p``;

const StreetAddress = styled.span`
  font-weight: 600;
`;

const CityName = styled.span`
  font-weight: 400;
`;

const StateAndZip = styled.span`
  font-weight: 200;
`;

type AddressWithStyleProps = {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
};

function AddressWithStyle({ street, city, state, zip }: AddressWithStyleProps) {
  return (
    <>
      <StreetAddress>{street}</StreetAddress>
      {', '}
      <CityName>{city}</CityName>
      {', '}
      <StateAndZip>
        {state} {zip}
      </StateAndZip>
    </>
  );
}

function prepareReviewForDisplay(review: Review) {
  return {
    ...review,
    createdTs: dayjs(review?.creationDate, 'YYYY-MM-DD hh:mm:ss').unix(),
  };
}

interface Review {
  creationDate: string; // ISO format?
}

interface Listing {
  reviews: Review[];
}

interface Property {
  licenseId: number | null;
  shortTermRental: boolean;
  address: string;
  strLicense: string | null;
  listings: Listing[];
}

interface Props {
  className?: string;
  property: Property;
  licenseData: unknown;
  isLicensingEnabled: boolean;
}

export function PropertyHistory({
  className,
  property: originalProperty,
  licenseData,
  isLicensingEnabled,
}: Props) {
  const licenses = useSelector(dataSelectors.licensesArray);

  const property = useMemo((): Property => {
    if (!originalProperty) return property;
    return {
      ...originalProperty,
      listings: originalProperty?.listings?.map((lst) => ({
        ...lst,
        reviews: lst?.reviews?.map(prepareReviewForDisplay),
      })),
    };
  }, [originalProperty]);

  const { address, shortTermRental } = property;

  const [displayedImageUrl, setDisplayedImageUrl] = useState(null);

  useEffect(() => {
    (async function prepareAndSignImageUrl() {
      // @ts-expect-error Property isn't typed well yet
      const imageUrl = firstImageUrl(property.imageUrls);
      const signedImageUrl = await signS3Url(imageUrl);
      const imageFullUrl = generateFullUrl(signedImageUrl);

      // @ts-expect-error generateFullUrl isn't typed well yet
      setDisplayedImageUrl(imageFullUrl);
    })();

    // @ts-expect-error Property isn't typed well yet
  }, [property?.imageUrls]);

  const parsedAddressComponents = useMemo(
    () => parseComponentsFromAddressSafe(address),
    [address]
  );

  const { __success: addressParsed } = parsedAddressComponents;

  return (
    <PropertyHistoryContainer className={className}>
      <PropertyDetails>
        <PropertyBasicDetails>
          <Address>
            {addressParsed ? (
              <AddressWithStyle {...parsedAddressComponents} />
            ) : (
              address
            )}
          </Address>
          <PropertyLicenseStatus
            shortTermRental={shortTermRental}
            strLicense={property.strLicense}
            licenseId={property.licenseId}
            licenses={licenses}
          />
        </PropertyBasicDetails>
        <PropertyImages>
          <PropertyReactImageFallback
            src={displayedImageUrl}
            fallbackImage={placeholderImg}
            alt=""
          />
          {/* @ts-expect-error Property isn't typed well yet */}
          <StyledStaticLocationMapImage
            // @ts-expect-error Property isn't typed well yet
            lnglat={[property.lnglat[0], property.lnglat[1]]}
            mapStyleSubpath={MAPBOX_DEFAULT_STYLE_SUBPATH}
          />
        </PropertyImages>
      </PropertyDetails>
      {/* @ts-expect-error MonthlyCalendar isn't typed yet */}
      <StyledCalendar
        property={property}
        licenseData={licenseData}
        showTOTCollectionMetrics={isLicensingEnabled}
      />
      <Footnote>* Figures are estimates only</Footnote>
    </PropertyHistoryContainer>
  );
}
